import { Youtube, Instagram, Send, Heart, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { BRAND } from "@/constants/brand";

const Footer = () => {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="relative border-t border-border" data-testid="site-footer">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(var(--brand-orange)/0.6), transparent)",
        }}
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-5 py-14 md:px-10 md:py-16">
        {/* Brand section on top */}
        <div className="mb-10 max-w-2xl md:mb-12">
          <Logo className="h-16 w-auto md:h-20" testId="footer-logo" />
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Buggu Capital — your trusted partner in stock market, investing &amp; wealth creation. Practical education, actionable strategies, zero hype.
          </p>
          <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
            Founder
          </p>
          <p className="font-display text-base font-semibold">Chandan Kumar Sah</p>
        </div>

        {/* Two-column layout: Explore links | Follow Along */}
        <div className="grid grid-cols-2 gap-6 md:gap-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-foreground">
              Explore
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                { l: "Home", h: "#home" },
                { l: "About", h: "#about" },
                { l: "Topics", h: "#topics" },
                { l: "Videos", h: "#videos" },
                { l: "Connect", h: "#connect" },
                { l: "Contact", h: "#contact" },
              ].map((it) => (
                <li key={it.h}>
                  <a
                    href={it.h}
                    className="text-muted-foreground transition-colors hover:text-[hsl(var(--brand-orange))]"
                    data-testid={`footer-link-${it.l.toLowerCase()}`}
                  >
                    {it.l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-foreground">
              Follow along
            </p>
            <div className="mt-4 flex flex-col gap-3">
              <a
                href={BRAND.socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-card/50 px-4 py-2 text-sm transition-all hover:-translate-y-0.5 hover:border-[hsl(0_90%_55%)] hover:text-[hsl(0_90%_55%)]"
                data-testid="footer-social-youtube"
              >
                <Youtube size={16} /> YouTube
              </a>
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-card/50 px-4 py-2 text-sm transition-all hover:-translate-y-0.5 hover:border-[hsl(320_85%_55%)] hover:text-[hsl(320_85%_55%)]"
                data-testid="footer-social-instagram"
              >
                <Instagram size={16} /> Instagram
              </a>
              <a
                href={BRAND.socials.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-card/50 px-4 py-2 text-sm transition-all hover:-translate-y-0.5 hover:border-[hsl(199_89%_55%)] hover:text-[hsl(199_89%_55%)]"
                data-testid="footer-social-telegram"
              >
                <Send size={16} /> Telegram
              </a>
            </div>
            <button
              onClick={scrollTop}
              className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground transition-colors hover:text-[hsl(var(--brand-orange))]"
              data-testid="footer-back-to-top"
            >
              ↑ Back to top
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 rounded-2xl border border-border bg-card/40 p-5">
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">Disclaimer:</span> Content on Buggu Capital is for educational and informational purposes only. It is not investment advice or a recommendation to buy or sell any security. Please do your own research or consult a SEBI-registered advisor before making financial decisions.
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 md:flex-row">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Buggu Capital. All rights reserved.
          </p>

          {/* Nodewap credit */}
          <a
            href={BRAND.agency.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-[hsl(var(--brand-orange))]"
            data-testid="nodewap-credit-link"
            aria-label={`Built with love by ${BRAND.agency.name} (opens in new tab)`}
          >
            <span>Built with</span>
            <Sparkles
              size={14}
              className="text-[hsl(var(--brand-orange))] transition-transform duration-300 group-hover:rotate-45"
              aria-hidden="true"
            />
            <span>
              by{" "}
              <span className="font-semibold text-foreground group-hover:text-[hsl(var(--brand-orange))]">
                {BRAND.agency.name}
              </span>
            </span>
          </a>

          <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            For the Indian investor community{" "}
            <Heart size={12} className="fill-[hsl(var(--brand-orange))] text-[hsl(var(--brand-orange))]" />
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
